import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";

const STATUS = {
  a_jour: { label: "À jour", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  a_synchroniser: { label: "À synchroniser", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  manquant: { label: "Manquant", cls: "bg-red-100 text-red-700 border-red-200" },
  vide: { label: "Vide", cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

const RegisterCard = ({ r, active, onFilter, onSync, onPdf, busy }) => (
  <Card className={`transition-shadow hover:shadow-md cursor-pointer ${active ? "ring-2 ring-blue-500" : ""}`}
    onClick={() => onFilter(active ? null : r.key)} data-testid={`register-card-${r.key}`}>
    <CardContent className="p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold leading-tight">{r.label}</p>
        <Badge variant="outline" className={`${STATUS[r.status].cls} text-[10px] shrink-0`}>{STATUS[r.status].label}</Badge>
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
        <span className="font-mono">{r.count} inscr.</span>
        {r.obligatoire && <Badge variant="outline" className="text-[10px]">Obligatoire</Badge>}
        {r.auto_sync && <span className="inline-flex items-center gap-0.5 text-purple-600"><Sparkles className="w-3 h-3" />auto</span>}
        {r.pending > 0 && <span className="text-amber-700">{r.pending} en attente</span>}
      </div>
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        {r.auto_sync && (
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => onSync(r.key)} data-testid={`register-sync-${r.key}`}>
            <RefreshCw className="w-3 h-3 mr-1" />Sync</Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600" onClick={() => onPdf(r)} data-testid={`register-pdf-${r.key}`}>
          <Eye className="w-3 h-3 mr-1" />PDF</Button>
      </div>
    </CardContent>
  </Card>
);

export const RegistersDashboard = ({ filter, onFilter, onChanged, refreshKey }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const load = () => api.get("/legal/registers/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [refreshKey]);

  const sync = async (type) => {
    setBusy(true);
    try {
      const r = await api.post(`/legal/registers/${type}/sync`);
      toast.success(`${r.data.created} inscription(s) ajoutée(s)`);
      load(); onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const syncAll = async () => {
    setBusy(true);
    try { const r = await api.post("/legal/registers/sync"); toast.success(`${r.data.total_created} inscription(s) ajoutée(s) dans tous les registres`); load(); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <div className="space-y-2" data-testid="registers-dashboard">
      <FilePreviewDialog file={preview} onClose={() => setPreview(null)} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
      <p className="text-sm text-slate-600">
        {data.forme_juridique} : <b>{data.a_jour}/{data.obligatoires}</b> registres obligatoires à jour. Clique un registre pour filtrer ses inscriptions.
      </p>
      <Button size="sm" onClick={syncAll} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="registers-sync-all">
        <RefreshCw className="w-3.5 h-3.5 mr-1" />Synchroniser tout</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
        {data.registers.map((r) => (
          <RegisterCard key={r.key} r={r} active={filter === r.key} onFilter={onFilter} onSync={sync} busy={busy}
            onPdf={(x) => setPreview({ src: `/legal/registers/${x.key}/pdf`, name: `Registre ${x.label}.pdf` })} />
        ))}
      </div>
    </div>
  );
};
